import React, { useEffect, useState } from 'react';
import { FaTag, FaDollarSign, FaCoins } from 'react-icons/fa';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';

export default function ProductPriceBreakdown({ priceComponent, costPriceComponent, priceTypeEnabled = false }) {
    const [priceComponents, setPriceComponents] = useState();
    const [expandedComponents, setExpandedComponents] = useState({});
    const [activePriceType, setActivePriceType] = useState('selling');

    useEffect(() => {
        setPriceComponents(priceComponent)
    }, [priceComponent])

    const handlePriceTypeChange = (value) => {
        if (priceTypeEnabled) {
            setActivePriceType(value);
            if (value === "cost")
                setPriceComponents(costPriceComponent)
            else if (value === "selling")
                setPriceComponents(priceComponent)
            else
                setPriceComponents(priceComponent)
        }
    }

    const toggleComponent = (componentKey) => {
        setExpandedComponents(prev => ({
            ...prev,
            [componentKey]: !prev[componentKey]
        }));
    };

    const formatCurrency = (amount, currency = 'INR') => {
        const formatters = {
            'INR': new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 2 }),
            'USD': new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }),
            'GBP': new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }),
            'EUR': new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }),
            'AED': new Intl.NumberFormat('ar-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 2 }),
            'SAR': new Intl.NumberFormat('ar-SA', { style: 'currency', currency: 'SAR', minimumFractionDigits: 2 })
        };

        return formatters[currency]?.format(amount) || `${currency} ${amount.toFixed(2)}`;
    };

    const formatKey = (key) => {
        return key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    const renderBreakdown = (breakdown) => {
        if (!breakdown || Object.keys(breakdown).length === 0) {
            return <small className="text-muted">No breakdown details available</small>;
        }

        if (Array.isArray(breakdown)) {
            return breakdown.map((item, index) => (
                <div key={index} className="bg-white rounded p-2 mb-1 border-start border-3 border-primary">
                    {Object.entries(item).map(([key, value]) => (
                        <div key={key} className="d-flex justify-content-between small py-1 border-bottom border-light">
                            <span className="text-muted">{formatKey(key)}</span>
                            <span className="fw-semibold">
                                {typeof value === 'number' && (key.includes("price") || key.includes("cost")) ? formatCurrency(value, 'INR') : value}
                            </span>
                        </div>
                    ))}
                </div>
            ));
        }

        if (typeof breakdown === 'object') {
            return (
                <div className="bg-white rounded p-2 border-start border-3 border-primary">
                    {Object.entries(breakdown).map(([key, value]) => (
                        <div key={key} className="d-flex justify-content-between small py-1 border-bottom border-light">
                            <span className="text-muted">{formatKey(key)}</span>
                            <span className="fw-semibold">
                                {typeof value === 'number' ? formatCurrency(value, 'INR') : value}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }

        return <small className="text-muted">Unknown breakdown format</small>;
    };

    const renderComponent = (component, data) => {
        const label = formatKey(component);
        const isExpanded = expandedComponents[component] || false;
        const hasBreakdown = data.breakdown && Object.keys(data.breakdown).length > 0;
        return (
            <div key={component} className="card border-0 shadow-sm rounded-3 mb-3">
                <div
                    className="card-header bg-white border-0 px-3 py-2 d-flex justify-content-between align-items-center"
                    style={{ cursor: hasBreakdown ? 'pointer' : 'default' }}
                    onClick={() => hasBreakdown && toggleComponent(component)}
                >
                    <div className="d-flex align-items-center">
                        <div>
                            <span className="fw-semibold">{label}</span>
                            <small className="text-muted ms-2">
                                {data.currency || 'INR'}
                            </small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center">
                        <span className={`fw-bold me-3`}>
                            {formatCurrency(data.amount_base, data.currency || 'INR')}
                        </span>
                        {hasBreakdown && (
                            <span className="text-muted">
                                {isExpanded ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
                            </span>
                        )}
                    </div>
                </div>
                {isExpanded && hasBreakdown && (
                    <div className="card-body bg-light pt-2 px-3 pb-3">
                        <div className="mb-2 small text-muted">
                            <strong>Breakdown:</strong>
                        </div>
                        {renderBreakdown(data.breakdown)}
                        <div className="w-100 text-end text-muted small me-2">X Quantity </div>
                        <div className="d-flex justify-content-end mt-2 border-top pt-2">
                            <span className="text-muted small">Total: </span>
                            <span className={`fw-bold ms-2`}>
                                {formatCurrency(data.amount_base, data.currency || 'INR')}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="mb-4">
            {priceTypeEnabled && (
                <div className="mb-4">
                    <div className="btn-group w-100" role="group" aria-label="Price type selector">
                        <button
                            type="button"
                            className={`btn ${activePriceType === 'cost' ? 'btn-primary' : 'btn-outline-secondary'} d-flex align-items-center justify-content-center gap-2 flex-fill`}
                            onClick={() => handlePriceTypeChange("cost")}
                            style={{
                                borderRadius: '0.375rem 0 0 0.375rem',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <FaCoins size={14} />
                            <span>Cost Price</span>
                        </button>
                        <button
                            type="button"
                            className={`btn ${activePriceType === 'selling' ? 'btn-primary' : 'btn-outline-secondary'} d-flex align-items-center justify-content-center gap-2 flex-fill`}
                            onClick={() => handlePriceTypeChange("selling")}
                            style={{
                                borderRadius: '0 0.375rem 0.375rem 0',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <FaDollarSign size={14} />
                            <span>Selling Price</span>
                        </button>
                    </div>
                    <div className="mt-2 text-center">
                        <small className="text-muted">
                            {activePriceType === 'cost' ? 'Showing cost price breakdown' : 'Showing selling price breakdown'}
                        </small>
                    </div>
                </div>
            )}

            {/* INR Components */}
            {priceComponents?.inrComponents && Object.keys(priceComponents?.inrComponents).length > 0 && (
                <>
                    <h6 className="text-muted small text-uppercase mb-3">
                        <FaTag className="me-1" /> Base Currency Components (INR)
                    </h6>
                    {Object.entries(priceComponents?.inrComponents).map(([component, data]) => {
                        return (
                            <React.Fragment key={component}>
                                {renderComponent(component, data)}
                            </React.Fragment>
                        );
                    })}
                </>
            )}

            {/* Local Components */}
            {priceComponents?.localComponents && Object.keys(priceComponents?.localComponents).length > 0 && (
                <>
                    <h6 className="text-muted small text-uppercase mt-4 mb-3">
                        <FaTag className="me-1" /> Local Currency Components
                    </h6>
                    {Object.entries(priceComponents?.localComponents).map(([component, data]) => {
                        return (
                            <React.Fragment key={component}>
                                {renderComponent(component, data)}
                            </React.Fragment>
                        );
                    })}
                </>
            )}
        </div>
    )
}